import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Converte cor hexadecimal para RGB
 */
const hexToRgb = (hex) => {
  // Remove o # se presente
  hex = hex.replace('#', '');
  
  // Converte para RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return [r, g, b];
};

/**
 * Converte URL de imagem para base64
 */
const imageUrlToBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      try {
        const dataURL = canvas.toDataURL('image/png');
        resolve({ dataURL, width: img.width, height: img.height });
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
};

/**
 * Gera comprovante de reserva em PDF
 */
export const generateReceipt = async (reservation, company) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Cores personalizadas da empresa ou padrão
  const primaryColor = company?.primaryColor 
    ? hexToRgb(company.primaryColor)
    : [102, 126, 234]; // #667eea
  const textColor = [51, 51, 51];
  const grayColor = [102, 102, 102];

  // Header com cor personalizada
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 40, 'F');

  // Logo da empresa (se existir)
  let yPos = 55;
  if (company?.logoUrl) {
    try {
      // Carregar logo como base64
      const logoData = await imageUrlToBase64(company.logoUrl);
      
      // Calcular dimensões do logo mantendo proporção
      const maxLogoHeight = 20;
      const maxLogoWidth = 50;
      let logoWidth = maxLogoWidth;
      let logoHeight = (logoData.height / logoData.width) * logoWidth;
      
      if (logoHeight > maxLogoHeight) {
        logoHeight = maxLogoHeight;
        logoWidth = (logoData.width / logoData.height) * logoHeight;
      }
      
      // Adicionar logo no canto esquerdo
      doc.addImage(logoData.dataURL, 'PNG', 15, 10, logoWidth, logoHeight);
      
      // Nome da empresa ao lado do logo
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text(company?.name || 'SDC Turismo', 70, 20);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text('Comprovante de Reserva', 70, 28);
    } catch (error) {
      // Se falhar ao adicionar logo, usar layout padrão centralizado
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text(company?.name || 'SDC Turismo', pageWidth / 2, 20, { align: 'center' });

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Comprovante de Reserva', pageWidth / 2, 30, { align: 'center' });
    }
  } else {
    // Layout padrão sem logo
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text(company?.name || 'SDC Turismo', pageWidth / 2, 20, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Comprovante de Reserva', pageWidth / 2, 30, { align: 'center' });
  }

  // Linha separadora com cor primária
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.5);
  doc.line(15, 45, pageWidth - 15, 45);

  // Status Badge
  doc.setFontSize(10);
  const statusConfig = {
    confirmed: { label: 'CONFIRMADA', color: [76, 175, 80] },
    completed: { label: 'CONCLUÍDA', color: [33, 150, 243] },
    pending: { label: 'PENDENTE', color: [255, 152, 0] },
    cancelled: { label: 'CANCELADA', color: [244, 67, 54] },
    no_show: { label: 'NÃO COMPARECEU', color: [100, 116, 139] }
  };

  const status = statusConfig[reservation.status] || statusConfig.pending;
  doc.setFillColor(...status.color);
  doc.roundedRect(pageWidth - 50, yPos - 5, 35, 8, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text(status.label, pageWidth - 32, yPos, { align: 'center' });

  // Número da Reserva
  doc.setTextColor(...textColor);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Nº da Reserva:', 15, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(reservation.id.substring(0, 8).toUpperCase(), 50, yPos);

  yPos += 10;

  // Data de emissão
  doc.setFont('helvetica', 'bold');
  doc.text('Data de Emissão:', 15, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleDateString('pt-BR'), 60, yPos);

  yPos += 15;

  // Seção Cliente
  doc.setFillColor(248, 249, 250);
  doc.rect(15, yPos, pageWidth - 30, 35, 'F');
  
  yPos += 8;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('DADOS DO CLIENTE', 20, yPos);

  yPos += 8;
  doc.setFontSize(10);
  doc.setTextColor(...textColor);
  doc.setFont('helvetica', 'bold');
  doc.text('Nome:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(reservation.clientName, 40, yPos);

  if (reservation.clientEmail) {
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Email:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(reservation.clientEmail, 40, yPos);
  }

  if (reservation.clientPhone) {
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Telefone:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(reservation.clientPhone, 40, yPos);
  }

  yPos += 15;

  // Seção Serviço
  doc.setFillColor(248, 249, 250);
  doc.rect(15, yPos, pageWidth - 30, 35, 'F');
  
  yPos += 8;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text('DETALHES DO SERVIÇO', 20, yPos);

  yPos += 8;
  doc.setFontSize(10);
  doc.setTextColor(...textColor);
  doc.setFont('helvetica', 'bold');
  doc.text('Serviço:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(reservation.serviceName, 45, yPos);

  if (reservation.roomName) {
    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Quarto:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(reservation.roomName, 45, yPos);
  }

  yPos += 6;
  doc.setFont('helvetica', 'bold');
  
  if (reservation.checkInDate && reservation.checkOutDate) {
    doc.text('Check-in:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    const checkIn = reservation.checkInDate.toDate ? reservation.checkInDate.toDate() : new Date(reservation.checkInDate);
    doc.text(checkIn.toLocaleDateString('pt-BR'), 45, yPos);

    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Check-out:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    const checkOut = reservation.checkOutDate.toDate ? reservation.checkOutDate.toDate() : new Date(reservation.checkOutDate);
    doc.text(checkOut.toLocaleDateString('pt-BR'), 45, yPos);

    yPos += 6;
    doc.setFont('helvetica', 'bold');
    doc.text('Diárias:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(String(reservation.dailyCount || 1), 45, yPos);
  } else {
    doc.text('Data/Hora:', 20, yPos);
    doc.setFont('helvetica', 'normal');
    const reservationDate = reservation.date.toDate ? reservation.date.toDate() : new Date(reservation.date);
    doc.text(reservationDate.toLocaleString('pt-BR'), 45, yPos);
  }

  yPos += 6;
  doc.setFont('helvetica', 'bold');
  doc.text('Participantes:', 20, yPos);
  doc.setFont('helvetica', 'normal');
  doc.text(String(reservation.participants), 50, yPos);

  yPos += 15;

  // Tabela de Valores
  const tableData = [
    ['Valor do Serviço', formatCurrency(reservation.totalAmount)],
    ['Valor Pago', formatCurrency(reservation.paidAmount)],
    ['Saldo', formatCurrency(reservation.totalAmount - reservation.paidAmount)]
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Descrição', 'Valor']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 11
    },
    bodyStyles: {
      fontSize: 10
    },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { halign: 'right', fontStyle: 'bold' }
    },
    margin: { left: 15, right: 15 }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Status do Pagamento
  const paymentStatusConfig = {
    paid: { label: '✓ PAGAMENTO CONFIRMADO', color: [76, 175, 80] },
    partial: { label: '⚠ PAGAMENTO PARCIAL', color: [255, 152, 0] },
    unpaid: { label: '✗ AGUARDANDO PAGAMENTO', color: [244, 67, 54] }
  };

  const paymentStatus = paymentStatusConfig[reservation.paymentStatus] || paymentStatusConfig.unpaid;
  doc.setFillColor(...paymentStatus.color);
  doc.roundedRect(15, yPos, pageWidth - 30, 12, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(paymentStatus.label, pageWidth / 2, yPos + 8, { align: 'center' });

  yPos += 20;

  // Observações (se houver)
  if (reservation.notes) {
    doc.setTextColor(...textColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Observações:', 15, yPos);
    
    yPos += 6;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayColor);
    const splitNotes = doc.splitTextToSize(reservation.notes, pageWidth - 30);
    doc.text(splitNotes, 15, yPos);
    yPos += splitNotes.length * 5;
  }

  // Footer
  const footerY = doc.internal.pageSize.height - 30;
  doc.setDrawColor(...grayColor);
  doc.setLineWidth(0.3);
  doc.line(15, footerY, pageWidth - 15, footerY);

  doc.setTextColor(...grayColor);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  yPos = footerY + 6;
  
  if (company?.address) {
    doc.text(company.address, pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
  }
  
  if (company?.phone || company?.email) {
    const contactInfo = [];
    if (company.phone) contactInfo.push(`Tel: ${company.phone}`);
    if (company.email) contactInfo.push(`Email: ${company.email}`);
    doc.text(contactInfo.join(' | '), pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
  }

  doc.setFontSize(8);
  doc.setTextColor(...grayColor);
  doc.text('Este é um comprovante gerado eletronicamente', pageWidth / 2, yPos, { align: 'center' });

  return doc;
};

/**
 * Baixa o comprovante como PDF
 */
export const downloadReceipt = async (reservation, company) => {
  const doc = await generateReceipt(reservation, company);
  const fileName = `Comprovante_${reservation.clientName.replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
};

/**
 * Abre o comprovante em nova aba
 */
export const openReceipt = async (reservation, company) => {
  const doc = await generateReceipt(reservation, company);
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

/**
 * Retorna o blob do PDF para envio por email
 */
export const getReceiptBlob = (reservation, company) => {
  const doc = generateReceipt(reservation, company);
  return doc.output('blob');
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};
